/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */

import React, { useContext } from 'react';
import classNames from 'classnames';

import { FormattedMessage, FormattedNumber } from '@futrli/localisation';
import { ContactDetails } from '@futrli-sdk/predict';

import { UIDonutChart } from '../../../../../ui/charts/donut-chart/donut-chart';
import { PredictManagementContext } from '../../../../../predict-management/context/predict-management-context';
import { penceToAmount } from '../../../../../core/formatters';
import { CUSTOMER_SUPPLIER_KIND } from '../const';

import * as styles from './dependency.scss';

const MIN_HIGH_RISK = 40;
const MIN_MEDIUM_RISK = 15;

export const Dependency = ({ activeData }: { activeData?: ContactDetails }) => {
  const { currency } = useContext(PredictManagementContext);

  if (!activeData) {
    return null;
  }

  const dependencyPercentage = Math.round(activeData.dependency_percent * 100);

  const graphColor =
    dependencyPercentage >= MIN_HIGH_RISK
      ? 'rgba(255, 0, 59, 0.45)'
      : dependencyPercentage >= MIN_MEDIUM_RISK
      ? 'rgba(255, 131, 48, 0.44)'
      : 'rgba(0, 246, 202, 0.49)';

  return (
    <>
      <div style={{ display: 'flex', marginTop: 32, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ marginRight: 16 }} className={classNames(styles.labelFont, styles.labelGrey)}>
          <FormattedMessage id="predict.dependency_last_90_days" defaultMessage="Dependency (last 90 days)" />
        </div>
        <div
          className={classNames(styles.dot, {
            [styles.lowPercentage]: dependencyPercentage < MIN_MEDIUM_RISK,
            [styles.mediumPercentage]: dependencyPercentage >= MIN_MEDIUM_RISK && dependencyPercentage < MIN_HIGH_RISK,
            [styles.bigPercentage]: dependencyPercentage >= MIN_HIGH_RISK
          })}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between' }}>
        <div className={styles.chartSection}>
          <UIDonutChart
            labels={['a', 'b']}
            testId="customers-suppliers-chart"
            datasets={[
              {
                data: [dependencyPercentage, 100 - dependencyPercentage],
                colors: [graphColor, '#EBF4F7']
              }
            ]}
            graphOptions={{
              rotation: -90,
              circumference: 180,
              borderWidth: 0,
              cutout: 30,
              layout: {}
            }}
            hideDataLabels={true}
          />
          <div className={styles.graphText}>
            <div>
              {dependencyPercentage}
              {'%'}
            </div>
            <div className={classNames(styles.labelFont, styles.labelGrey)}>
              <FormattedMessage id="predict.last_90_days" defaultMessage="Last 90 days" />
            </div>
          </div>
        </div>
        <div style={{ marginBottom: 2 }}>
          <div className={styles.graphTextFont}>
            <FormattedNumber
              style="currency"
              currency={currency!}
              value={penceToAmount(activeData.dependency_contact_amount)}
              minimumFractionDigits={0}
              maximumFractionDigits={0}
            />
          </div>
          <div className={classNames(styles.labelFont, styles.labelGrey)}>
            {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
              <FormattedMessage id="predict.their_sales" defaultMessage="Their sales" />
            ) : (
              <FormattedMessage id="predict.their_purchases" defaultMessage="Their purchases" />
            )}
          </div>
        </div>
        <div style={{ marginBottom: 2 }}>
          <div className={styles.graphTextFont}>
            <FormattedNumber
              style="currency"
              currency={currency!}
              value={penceToAmount(activeData.dependency_total_amount)}
              minimumFractionDigits={0}
              maximumFractionDigits={0}
            />
          </div>
          <div className={classNames(styles.labelFont, styles.labelGrey)}>
            {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
              <FormattedMessage id="predict.total_sales" defaultMessage="Total sales" />
            ) : (
              <FormattedMessage id="predict.total_purchases" defaultMessage="Total purchases" />
            )}
          </div>
        </div>
      </div>
      {activeData.excluded_invoices > 0 && (
        <div className={styles.basicFont} style={{ fontWeight: 400, marginTop: 32 }}>
          {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
            <FormattedMessage
              id="predict.you_have_excluded_value_invoiced"
              defaultMessage="You have excluded {excludedNum} invoices, to the value of {excludedValue} for this customer."
              values={{
                excludedNum: activeData.excluded_invoices,
                excludedValue: (
                  <FormattedNumber
                    style="currency"
                    currency={currency!}
                    value={penceToAmount(activeData.amount_excluded)}
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                )
              }}
            />
          ) : (
            <FormattedMessage
              id="predict.you_have_excluded_value_bills"
              defaultMessage="You have excluded {excludedNum} bills, to the value of {excludedValue} for this supplier."
              values={{
                excludedNum: activeData.excluded_invoices,
                excludedValue: (
                  <FormattedNumber
                    style="currency"
                    currency={currency!}
                    value={penceToAmount(activeData.amount_excluded)}
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                )
              }}
            />
          )}
        </div>
      )}
    </>
  );
};
